import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from './services/api';
import { useBrandingConfig } from './BrandingConfigContext';

function ResetPasswordPage() {
  const { brandingConfig } = useBrandingConfig();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    error: '',
    success: '',
  });

  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setFormData(prev => ({ ...prev, error: 'No reset token provided' }));
      setIsLoading(false);
      return;
    }

    // Verify the reset token
    const verifyToken = async () => {
      try {
        await api.verifyResetToken(token);
        setIsValidToken(true);
      } catch (error) {
        setFormData(prev => ({
          ...prev,
          error: 'Invalid or expired reset token. Please request a new password reset.',
        }));
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      error: '',
      success: '',
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setFormData(prev => ({
        ...prev,
        error: 'Passwords do not match',
      }));
      return;
    }

    if (formData.password.length < 6) {
      setFormData(prev => ({
        ...prev,
        error: 'Password must be at least 6 characters long',
      }));
      return;
    }

    setIsSubmitting(true);

    try {
      await api.resetPassword(token, formData.password);
      setFormData(prev => ({
        ...prev,
        success: 'Password has been reset successfully! Redirecting to login...',
      }));

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        error: error.message || 'Failed to reset password',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl shadow-lg mb-4">
              <img src="/SWARM_logo_no_tagline.svg" alt="SWARM Logo" className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-bold text-primary-800 mb-2">SWARM</h1>
            <p className="text-lg text-primary-600 font-medium mb-1">Incident Reporter</p>
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-primary-600">S</span>afer{' '}
              <span className="font-semibold text-primary-600">W</span>alking{' '}
              <span className="font-semibold text-primary-600">A</span>nd{' '}
              <span className="font-semibold text-primary-600">R</span>iding on{' '}
              <span className="font-semibold text-primary-600">M</span>urray rd
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying reset token...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl shadow-lg mb-4">
              <img src="/SWARM_logo_no_tagline.svg" alt="SWARM Logo" className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-bold text-primary-800 mb-2">SWARM</h1>
            <p className="text-lg text-primary-600 font-medium mb-1">Incident Reporter</p>
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-primary-600">S</span>afer{' '}
              <span className="font-semibold text-primary-600">W</span>alking{' '}
              <span className="font-semibold text-primary-600">A</span>nd{' '}
              <span className="font-semibold text-primary-600">R</span>iding on{' '}
              <span className="font-semibold text-primary-600">M</span>urray rd
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reset Password</h2>
            {formData.error && <div className="form-error">{formData.error}</div>}
            <div className="mt-6">
              <button onClick={() => navigate('/')} className="btn btn-primary btn-small w-full">
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* SWARM Logo and Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl shadow-lg mb-4">
            {brandingConfig?.logo_url ? (
              <img src={brandingConfig.logo_url} alt="Logo" className="w-12 h-12 rounded" />
            ) : (
              <img src="/SWARM_logo_no_tagline.svg" alt="SWARM Logo" className="w-12 h-12" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-primary-800 mb-2">
            {brandingConfig?.site_name || 'SWARM'}
          </h1>
          {brandingConfig?.strapline && (
            <p className="text-lg text-primary-600 font-medium mb-1">{brandingConfig.strapline}</p>
          )}
          <p className="text-xs text-gray-500">Reset Password</p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h2>
            <p className="text-gray-600">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                New Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your new password"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Confirm your new password"
                required
                minLength={6}
              />
            </div>

            {formData.error && <div className="form-error">{formData.error}</div>}
            {formData.success && <div className="form-success">{formData.success}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-small w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>
            © 2026{' '}
            <a
              href="https://anthemic-developments.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              anthemic-developments.com
            </a>
          </p>
          <p className="mt-1">Making communities safer, one report at a time.</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
